import React, { useMemo } from "react";
import { Form } from "react-final-form";

import { selectVersionConfig } from "fond/api";
import { Maybe, ScenarioConfigurationForm, VersionScenarioConfiguration } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import ConfigurationStep from "./ConfigurationStep";
import { convertConfigToFormData } from "./helper";
import LayerSelectionStep from "./LayerSelectionStep";
import { Steps } from "./SetupModal";
import UploadConfigurationStep from "./UploadConfigurationStep";

export interface FormData extends ScenarioConfigurationForm {
  // Files uploaded by the user to the form, but not directly sent in the payload
  Files: {
    Occupancy: File | null;
    Artifacts: File[];
  };
  DefaultBins: boolean;
}

const defaults: FormData = {
  Files: {
    Occupancy: null,
    Artifacts: [],
  },
  DefaultBins: false,
  ToolConfigurations: {
    CostToServe: {
      CostingLayer: null,
      BinCount: 5,
      AdditionalSettings: `{
    "root_node_filter": [
        "all",
        ["==", ["prop", ["elementData"], "Tier"], "T3"],
        ["==", ["prop", ["elementData"], "Type"], "HUB"]
    ]
}`,
    },
    DesignPhasePartitioner: {
      AdditionalSettings: `{
    "root_node_filter": [
        "all",
        ["==", ["prop", ["elementData"], "Tier"], "T3"],
        ["==", ["prop", ["elementData"], "Type"], "HUB"]
    ]
}`,
    },
  },
  Inputs: {
    BillOfMaterials: {
      Type: "file",
      Data: null,
    },
    CostBreakdown: {
      Type: "file",
      Data: null,
    },
    Occupancy: {
      Type: "layer",
      Layer: { LayerKey: null },
    },
    ToolLayersConfiguration: {
      LayersConfiguration: {
        Type: "file",
        Data: null,
      },
      Mappings: [],
    },
  },
};

interface SetupFormProps {
  activeStep: Steps;
  configuration: Maybe<VersionScenarioConfiguration>;
  isReadOnly?: boolean;
  onSubmit: (values: FormData) => void;
  onNext: () => void;
}

const SetupForm: React.FC<SetupFormProps> = ({ activeStep, configuration, isReadOnly, onNext, onSubmit }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const initialValues = useMemo(() => {
    if (configuration) {
      return convertConfigToFormData(configuration);
    }
    return defaults;
  }, [configuration]);

  const handleOnSubmit = (values: FormData) => {
    if (activeStep < Steps.CONFIGURATION) {
      onNext();
    } else {
      onSubmit(values);
    }
  };

  const renderStepContent = (step: Steps) => {
    switch (step) {
      case Steps.UPLOAD:
        return <UploadConfigurationStep isReadOnly={isReadOnly} />;
      case Steps.LAYER_SELECTION:
        return <LayerSelectionStep config={config} isReadOnly={isReadOnly} />;
      case Steps.CONFIGURATION:
        return <ConfigurationStep config={config} isReadOnly={isReadOnly} />;
      default:
        return null;
    }
  };

  return (
    <Form<FormData>
      onSubmit={handleOnSubmit}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form id="collaboration-cts-form" onSubmit={handleSubmit}>
          {renderStepContent(activeStep)}
        </form>
      )}
    />
  );
};

export default SetupForm;
